import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Loader2Icon } from 'lucide-react'
import { useMemo } from 'react'

import { useGetUserById } from '@/api/get-user-by-id.hook'

import { LEMA_USER_ID } from '@/const/contact'

import { formatTimestamp, timeSince } from '@/lib/date/date'
import { useLastEngagementIrqChange } from '@/lib/risk/last-engagement-irq-change'
import { cn } from '@/lib/ui/style-helpers'

import { USER_SCORE_INFO_LABELS } from '@/pages/company-drawer/company-overview/user-score-info/labels'

import { UserScoreDisplay } from '@/components/shared/user-score-display/user-score-display'

type UserLastEngagementIrqUpdateInfoProps = {
  companyId: string
  engagementId: string
}

export const UserLastEngagementIrqUpdateInfo = ({
  companyId,
  engagementId,
}: UserLastEngagementIrqUpdateInfoProps) => {
  const { lastIRQChange, isLoading: isLastIrqChangeLoading } = useLastEngagementIrqChange(
    companyId,
    engagementId,
  )

  const integrationSource = lastIRQChange.integrationSource
  const userResult = useGetUserById(lastIRQChange.userId)
  const isLoadingUser = !integrationSource && 'isLoading' in userResult && userResult.isLoading
  const user = userResult.user

  const timeSinceLastScorer = useMemo(() => {
    if (!lastIRQChange.time) return null
    const { value, unit } = timeSince(timestampDate(lastIRQChange.time))
    return `${value} ${unit} ago`
  }, [lastIRQChange.time])

  if (isLastIrqChangeLoading || isLoadingUser) {
    return <Loader2Icon className='size-4 animate-spin' />
  }

  const isLemaScored = lastIRQChange.userId === LEMA_USER_ID

  return (
    <span className={cn('flex items-center gap-1.5 text-nowrap text-body-small')}>
      {integrationSource ? (
        <span className='text-nowrap text-main-foreground-tertiary text-body-small'>
          {USER_SCORE_INFO_LABELS.SCORED_VIA_INTEGRATION}
        </span>
      ) : (
        <>
          <span className='text-nowrap text-main-foreground-tertiary'>
            {isLemaScored ? 'Scored by' : 'Updated by'}
          </span>
          <UserScoreDisplay userId={lastIRQChange.userId} userName={user?.name} />
        </>
      )}
      {timeSinceLastScorer && (
        <span className='text-nowrap text-main-foreground-tertiary'>
          on {formatTimestamp(lastIRQChange.time, 'shortDateTime')} ({timeSinceLastScorer})
        </span>
      )}
    </span>
  )
}
